import { cloneDeep } from 'lodash-es'
import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import { StorePerformance } from '@/models'
import { DisplayPerformance, DisplayShow } from '@/modules/guestUi/interfaces/shows'
import { useSettingStore } from '@/stores/setting'
import { sanitizeHtmlForClient } from '@/utils/sanitize'
import { sortBy } from '@/utils/utils'

import type { PerformanceId } from '@generated/types'

export interface UseDisplayShowsOptions {
  // eslint-disable-next-line no-unused-vars
  filter?: (p: StorePerformance) => boolean
  idsToForcePurchase?: MaybeRefOrGetter<PerformanceId[]>
}

export function useDisplayShows(
  performances: MaybeRefOrGetter<StorePerformance[]>,
  options: UseDisplayShowsOptions = {},
) {
  const settingStore = useSettingStore()
  const { isShowGridOptimizationEnabled } = useFeatureFlags()

  return computed<DisplayShow[]>(() => {
    const list = toValue(performances)
    const idsToForcePurchase = toValue(options.idsToForcePurchase) ?? []
    const filter = options.filter ?? (() => true)

    if (isShowGridOptimizationEnabled.value) {
      const idsSet = new Set<PerformanceId>(idsToForcePurchase)
      const groupMap = new Map<string, DisplayShow>()

      for (const performance of list) {
        if (!filter(performance)) continue

        const displayPerformance: DisplayPerformance = {
          ...performance,
          forcePurchase: idsSet.has(performance.id),
        }

        const key = `${performance.show_id}|${performance.date}`
        const existing = groupMap.get(key)

        if (existing) {
          existing.performances.push(displayPerformance)
        } else {
          groupMap.set(key, {
            ...displayPerformance.show,
            id: displayPerformance.show_id,
            date: displayPerformance.date,
            datetime: displayPerformance.datetime,
            dateStr: settingStore.mediumDate(displayPerformance.datetime),
            description: displayPerformance.show?.description ?? '',
            position: displayPerformance.show?.position ?? [],
            performances: [displayPerformance],
            sanitizedDescription: sanitizeHtmlForClient(displayPerformance.show?.description ?? ''),
          })
        }
      }

      const shows = Array.from(groupMap.values())
      for (const show of shows) {
        sortBy(show.performances, 'datetime')
        show.firstResidencySlug =
          show.performances.flatMap(p => p.residencies ?? [])[0]?.slug ?? null
      }
      return sortBy(shows, 'datetime')
    }

    const shows: DisplayShow[] = []

    for (const performance of list) {
      if (!filter(performance)) continue

      const existing = shows.find(s => s.date === performance.date && s.id === performance.show_id)
      const displayPerformance = cloneDeep(performance) as DisplayPerformance
      displayPerformance.forcePurchase = idsToForcePurchase.includes(performance.id)

      if (existing) {
        existing.performances.push(displayPerformance)
        sortBy(existing.performances, 'datetime')
      } else {
        shows.push({
          ...displayPerformance.show,
          id: displayPerformance.show_id,
          date: displayPerformance.date,
          datetime: displayPerformance.datetime,
          dateStr: settingStore.mediumDate(displayPerformance.datetime),
          description: displayPerformance.show?.description ?? '',
          position: displayPerformance.show?.position ?? [],
          performances: [displayPerformance],
        })
      }
    }

    return sortBy(shows, 'datetime')
  })
}
